import { useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Shield, RotateCcw, FileText } from 'lucide-react';

// ─── POLICY CONTENT ───────────────────────────────────────────────────────────

const PRIVACY_POLICY = {
  title: 'Privacy Policy',
  icon: Shield,
  lastUpdated: '14 Aug 2026',
  sections: [
    {
      type: 'intro',
      text: 'At hhouseofbreadss ("we," "us," "our," or "the Bakery"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, store, share, and protect your data when you visit our website hhouseofbreadss.com (the "Site") or purchase our bakery Products. By using our website and services, you consent to the practices described in this Privacy Policy.'
    },
    {
      heading: '1. Information We Collect',
      subsections: [
        {
          subheading: '1.1 Information You Provide Directly',
          text: 'When you register, place an order, or contact us, we may collect:',
          bullets: [
            'Personal Details: Name, email address, phone number, and delivery address.',
            'Order Details: Products ordered, quantities, delivery instructions, and order history.',
            'Payment Information: Payment method details (e.g., UPI ID, card type). We do not store your full card number, CVV, OTP, or net banking credentials. All sensitive payment data is handled directly by our Payment Gateway partner, RazorPay.',
            'Communication Data: Messages, feedback, reviews, or queries you send via email, phone, social media, or contact forms.'
          ]
        },
        {
          subheading: '1.2 Information Collected Automatically',
          text: 'When you browse our website, we may automatically collect:',
          bullets: [
            'Device & Browser Information: IP address, browser type, operating system, and device type.',
            'Usage Data: Pages visited, time spent on pages, click patterns, and referring URLs.',
            'Cookies & Tracking Technologies: Session cookies, analytics cookies, and similar technologies (see Section 6).'
          ]
        },
        {
          subheading: '1.3 Information from Third Parties',
          text: 'We may receive information from:',
          bullets: [
            'Payment Gateways (e.g., RazorPay) — Transaction status and payment confirmation.',
            'Delivery Partners — Delivery status and confirmation.',
            'Social Media Platforms — If you interact with our social media pages or log in via social accounts.'
          ]
        }
      ]
    },
    {
      heading: '2. How We Use Your Information',
      bullets: [
        '2.1 To Process and Fulfill Orders: Confirming orders, preparing Products, arranging delivery, and providing order updates.',
        '2.2 To Process Payments: Verifying and authorizing payments through RazorPay, issuing refunds, and preventing fraudulent transactions.',
        '2.3 Customer Support: Responding to your queries, complaints, and requests.',
        '2.4 Communication: Sending order confirmations, delivery updates, promotional offers, and service notifications.',
        '2.5 Personalization: Tailoring product recommendations and offers based on your preferences and order history.',
        '2.6 Website Improvement: Analyzing usage patterns to improve website functionality and user experience.',
        '2.7 Legal Compliance: Complying with applicable laws, regulations, and legal obligations including tax and accounting requirements.',
        '2.8 Security & Fraud Prevention: Detecting, preventing, and addressing fraud, unauthorized transactions, and security issues.'
      ]
    },
    {
      heading: '3. Legal Basis for Processing (DPDP Act Compliance)',
      text: 'We process your personal data on the following legal bases:',
      bullets: [
        'Consent: You have explicitly or implicitly consented to the processing of your data.',
        'Contractual Necessity: Processing is necessary to fulfill our contractual obligations to you (e.g., delivering your order).',
        'Legal Obligation: Processing is required to comply with applicable laws and regulations.',
        'Legitimate Interest: Processing is in our legitimate business interest (e.g., fraud prevention, website security).'
      ],
      footer: 'hhouseofbreadss complies with the Digital Personal Data Protection (DPDP) Act, 2023 of India and other applicable data protection regulations.'
    },
    {
      heading: '4. How We Share Your Information',
      text: 'We do not sell, rent, or trade your personal information to third parties. However, we may share your data with:',
      subsections: [
        { subheading: '4.1 Payment Gateway Partners', text: 'RazorPay and other payment processors to authorize and process your transactions securely. These partners process your payment data under their own privacy policies and security standards (PCI-DSS compliant).' },
        { subheading: '4.2 Delivery / Logistics Partners', text: 'To deliver your Orders, we share your name, address, phone number, and order details with third-party courier/delivery services.' },
        { subheading: '4.3 Service Providers', text: 'Website hosting and maintenance, analytics and performance tracking, email/SMS service providers for order updates and marketing, and customer support tools.' },
        { subheading: '4.4 Legal & Regulatory Authorities', text: 'We may disclose your information if required by law, court order, or government authority, or to protect our legal rights, safety, or property.' },
        { subheading: '4.5 Business Transfers', text: 'In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to applicable data protection laws.' }
      ]
    },
    {
      heading: '5. Data Retention',
      text: 'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy.',
      table: {
        headers: ['Data Type', 'Retention Period'],
        rows: [
          ['Account Data', 'Duration of account + 3 years after closure'],
          ['Order & Transaction Data', 'Up to 7 years (tax/legal compliance)'],
          ['Marketing Data', 'Until you unsubscribe or withdraw consent'],
          ['Cookies', 'As per cookie duration specified in Section 6']
        ]
      },
      footer: 'After the retention period, your data will be securely deleted or anonymized.'
    },
    {
      heading: '6. Cookies & Tracking Technologies',
      table: {
        headers: ['Cookie Type', 'Purpose', 'Duration'],
        rows: [
          ['Essential Cookies', 'Enable core website functionality (shopping cart, login)', 'Session-based'],
          ['Analytics Cookies', 'Track website usage and performance', 'Up to 24 months'],
          ['Functional Cookies', 'Remember preferences (language, location)', 'Up to 12 months'],
          ['Marketing Cookies', 'Deliver relevant advertisements and promotions', 'Up to 6 months']
        ]
      },
      footer: 'You can manage or disable cookies through your browser settings. Disabling certain cookies may affect website functionality.'
    },
    {
      heading: '7. Data Security',
      text: 'Our security measures include:',
      bullets: [
        'SSL/TLS Encryption: All data transmitted between your browser and our website is encrypted.',
        'Secure Payment Processing: Payment transactions are handled by RazorPay, which is PCI-DSS Level 1 certified. We never store your card number, CVV, or OTP.',
        'Access Controls: Restricted, role-based access to personal data within our organization.',
        'Regular Audits: Periodic security reviews and vulnerability assessments.'
      ],
      footer: 'In the event of a data breach, hhouseofbreadss will notify affected users and relevant authorities within 72 hours of becoming aware of the breach, in accordance with the DPDP Act, 2023.'
    },
    {
      heading: '8. Your Rights (Data Subject Rights)',
      bullets: [
        '8.1 Right to Access: Request a copy of the personal data we hold about you.',
        '8.2 Right to Correction: Request correction of inaccurate or incomplete personal data.',
        '8.3 Right to Erasure ("Right to be Forgotten"): Request deletion of your personal data, subject to legal retention obligations.',
        '8.4 Right to Data Portability: Request your data in a structured, machine-readable format.',
        '8.5 Right to Object: Object to the processing of your data for marketing or other legitimate interest purposes.',
        '8.6 Right to Withdraw Consent: Withdraw consent at any time.'
      ],
      footer: 'To exercise any of these rights, contact us at hhouseofbreadss@gmail.com with the subject line "Data Rights Request." We will respond within 30 days.'
    },
    {
      heading: '9. Children\'s Privacy',
      text: 'Our website and services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately.'
    },
    {
      heading: '10. Third-Party Links',
      text: 'Our website may contain links to third-party websites (e.g., social media pages, delivery partner websites, RazorPay). This Privacy Policy does not apply to those external sites. Please review their privacy policies separately.'
    },
    {
      heading: '11. Social Media',
      text: 'If you interact with hhouseofbreadss on social media platforms (Instagram, Facebook, WhatsApp), the respective platform\'s privacy policy applies to your interactions. We do not control how social media platforms collect, use, or share your data.'
    },
    {
      heading: '12. International Data Transfers',
      text: 'hhouseofbreadss operates in India, and your personal data is primarily stored and processed in India. If we engage third-party service providers located outside India, we ensure appropriate data transfer safeguards are in place in compliance with the DPDP Act, 2023.'
    },
    {
      heading: '13. Changes to This Privacy Policy',
      text: 'We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated Policy on our website with a revised "Last Updated" date and/or sending an email notification. Your continued use constitutes acceptance of the revised Policy.'
    },
    {
      heading: '14. Contact Information',
      contact: true
    }
  ]
};

const REFUND_POLICY = {
  title: 'Refund & Cancellation Policy',
  icon: RotateCcw,
  lastUpdated: '14 Aug 2026',
  sections: [
    {
      type: 'intro',
      text: 'At hhouseofbreadss, we are committed to providing high-quality bakery Products and excellent customer service. This Refund & Cancellation Policy outlines the terms under which you can cancel an order, request a refund, or report a damaged or incorrect product. By placing an order on our website hhouseofbreadss.com, you agree to the terms of this Policy.'
    },
    {
      heading: '1. Order Cancellation',
      subsections: [
        {
          subheading: '1.1 Cancellation by Customer',
          table: {
            headers: ['Timing', 'Cancellation Status', 'Refund'],
            rows: [
              ['Before order preparation begins', '✅ Allowed', 'Full refund (less any payment gateway charges, if applicable)'],
              ['After preparation has started', '❌ Not allowed', 'No refund (bakery items are made-to-order & perishable)'],
              ['After dispatch/delivery', '❌ Not allowed', 'No refund']
            ]
          },
          text: 'To cancel an eligible order, contact us at hhouseofbreadss@gmail.com or call +91-9999183418 with your Order ID. Cancellation requests must be made before the scheduled preparation/delivery time.'
        },
        {
          subheading: '1.2 Cancellation by hhouseofbreadss',
          text: 'We reserve the right to cancel an order under the following circumstances:',
          bullets: [
            'Product unavailability or stock shortage',
            'Pricing or description errors on the website',
            'Payment failure, suspected fraud, or unauthorized transaction',
            'Delivery to an area outside our serviceable zones',
            'Force majeure events'
          ],
          footer: 'In all such cases, a full refund will be initiated to the original payment method.'
        },
        {
          subheading: '1.3 Customized / Made-to-Order Products',
          text: 'Products that are customized (e.g., personalized cakes, themed desserts) cannot be cancelled once preparation has begun. Customized orders can only be cancelled if the request is made at least 48 hours before the scheduled delivery/pickup time, subject to our confirmation.'
        }
      ]
    },
    {
      heading: '2. Refund Policy',
      subsections: [
        {
          subheading: '2.1 Eligibility for Refund',
          table: {
            headers: ['Scenario', 'Refund Eligible?', 'Condition'],
            rows: [
              ['Order cancelled before preparation', '✅ Yes', 'Full refund (less gateway charges if any)'],
              ['Order cancelled by hhouseofbreadss', '✅ Yes', 'Full refund'],
              ['Failed payment / amount debited but order not confirmed', '✅ Yes', 'Refund via Payment Gateway'],
              ['Damaged product on delivery', '✅ Yes (or replacement)', 'Report within 24 hours with proof'],
              ['Spoiled or stale product', '✅ Yes (or replacement)', 'Report within 24 hours with proof'],
              ['Wrong product delivered', '✅ Yes (or replacement)', 'Report within 24 hours with proof'],
              ['Product does not match description', '✅ Yes (at our discretion)', 'Report within 24 hours with proof'],
              ['Change of mind / personal preference', '❌ No', 'Not eligible for refund'],
              ['Product consumed partially or fully', '❌ No', 'Not eligible for refund'],
              ['Order not cancelled in time (perishable goods)', '❌ No', 'Not eligible for refund']
            ]
          }
        },
        {
          subheading: '2.2 Refund Processing Timeline',
          table: {
            headers: ['Refund Method', 'Processing Time', 'Notes'],
            rows: [
              ['RazorPay (UPI / Cards / Net Banking / Wallets)', '5–7 business days', 'Refund to original payment method'],
              ['Bank Transfer (NEFT/IMPS)', '5–7 business days', 'For cases where original method refund is not possible'],
              ['Store Credit / Wallet', '1–2 business days', 'If you opt for store credit instead of a refund']
            ]
          },
          footer: 'Refunds are processed only after verification and approval by hhouseofbreadss. The actual time may take up to 10–14 business days in some cases.'
        },
        {
          subheading: '2.3 Refund for Failed Transactions',
          text: 'If your payment is debited but the order is not confirmed: hhouseofbreadss will verify the failed transaction with RazorPay. If confirmed as failed, a refund will be initiated within 3–5 business days to your original payment method. Please contact us with your transaction reference number / payment ID and Order ID.'
        },
        {
          subheading: '2.4 Partial Refunds',
          text: 'In certain cases, hhouseofbreadss may offer a partial refund at its sole discretion, such as:',
          bullets: [
            'A portion of the order is unavailable or undeliverable.',
            'Minor deviation from the product description.',
            'Delivery delay beyond the promised time frame (at our discretion).'
          ]
        }
      ]
    },
    {
      heading: '3. Return & Replacement Policy',
      subsections: [
        {
          subheading: '3.1 No-Return Policy for Perishable Goods',
          text: 'Bakery Products are perishable food items and are exempt from the standard return policy under applicable consumer protection laws. Returns are not accepted due to hygiene and food safety concerns.'
        },
        {
          subheading: '3.2 Replacement for Damaged / Defective Products',
          text: 'If you receive a damaged, spoiled, or significantly different product, you must:',
          bullets: [
            'Report the issue within 24 hours of delivery by emailing hhouseofbreadss@gmail.com or calling +91-9999183418.',
            'Provide your Order ID and delivery date.',
            'Submit clear photographic evidence of the damaged/defective Product.',
            'Retain the original packaging for verification if required.'
          ],
          footer: 'Upon verification, hhouseofbreadss will arrange a replacement delivery at no additional cost (if available), OR issue a full refund to the original payment method. Replacement/refund will NOT be granted if: report submitted after 24 hours, insufficient photographic evidence, product appears consumed, damage caused by customer after delivery, minor variation in appearance due to handcrafted nature, or flavor/taste preferences.'
        }
      ]
    },
    {
      heading: '4. Delivery-Related Issues',
      subsections: [
        {
          subheading: '4.1 Non-Delivery',
          text: 'If your order is not delivered within the estimated delivery window, contact us within 48 hours. hhouseofbreadss will investigate and either arrange re-delivery or issue a full refund if the order is confirmed lost/undelivered.'
        },
        {
          subheading: '4.2 Incorrect Delivery Address',
          text: 'hhouseofbreadss is not responsible for non-delivery or mis-delivery caused by incorrect, incomplete, or outdated address details provided by the customer. No refund will be issued in such cases.'
        }
      ]
    },
    {
      heading: '5. Payment Gateway (RazorPay) Specific Terms',
      bullets: [
        'All online payments on hhouseofbreadss.com are processed through RazorPay, a PCI-DSS Level 1 certified payment gateway.',
        'Refunds are initiated to the original payment method (UPI, card, net banking, wallet) used during checkout.',
        'Once hhouseofbreadss initiates a refund, RazorPay processes it and the amount is typically reflected in your account within 5–7 business days.',
        'Your bank may take additional time (up to 14 business days) to credit the refund.',
        'For payment-related queries: RazorPay Support — support@razorpay.com | razorpay.com'
      ]
    },
    {
      heading: '6. Store Credit Policy',
      bullets: [
        'Store credit may be offered in lieu of monetary refund in certain cases (partial refunds, goodwill gestures, delivery delays).',
        'Valid for 6 months from the date of issuance unless otherwise specified.',
        'Can be used for any product on the website, subject to availability.',
        'Non-transferable, non-exchangeable for cash, and cannot be combined with other offers unless stated.'
      ]
    },
    {
      heading: '7. Force Majeure',
      text: 'hhouseofbreadss shall not be liable for refunds, cancellations, or delivery failures caused by events beyond its reasonable control, including:',
      bullets: [
        'Natural disasters (floods, earthquakes, storms)',
        'Pandemics or epidemics',
        'Government actions, restrictions, or lockdowns',
        'Labor strikes or civil unrest',
        'Internet, telecommunications, or power outages',
        'Courier/delivery partner failures due to force majeure'
      ],
      footer: 'In such cases, hhouseofbreadss will make reasonable efforts to fulfill the order or offer store credit at its discretion.'
    },
    {
      heading: '8. How to Request a Cancellation, Refund, or Replacement',
      text: 'Email us at hhouseofbreadss@gmail.com with the subject line: "Refund/Cancellation Request – Order ID: [Your Order ID]" or call +91-9999183418 (Mon–Sat, 9:00 AM – 6:00 PM).',
      text2: 'Please provide:',
      bullets: [
        'Order ID',
        'Date of order / delivery',
        'Reason for cancellation/refund/replacement',
        'Photographic evidence (for damaged/defective products)',
        'Transaction ID / Payment ID (for payment-related issues)'
      ],
      footer: 'Our team will review your request and respond within 24–48 hours.'
    },
    {
      heading: '9. Grievance Redressal',
      text: 'If you are unsatisfied with the resolution provided, you may escalate the matter to our Grievance Officer:',
      bullets: [
        'Grievance Officer: Anubhuti Singh',
        'Email: hhouseofbreadss@gmail.com',
        'Phone: +91-9999183418',
        'Address: R3/015A, M3M Cosmopolitan, Sec 66, Gurugram, Haryana'
      ],
      footer: 'We are committed to resolving all grievances within 15 business days. If unresolved, you may approach the relevant Consumer Dispute Redressal Forum under the Consumer Protection Act, 2019.'
    },
    {
      heading: '10. Changes to This Policy',
      text: 'hhouseofbreadss reserves the right to modify this Refund & Cancellation Policy at any time. The updated Policy will be posted on our website with a revised "Last Updated" date. The Policy applicable to your order is the one in effect at the time you placed the order.'
    },
    {
      heading: '11. Contact Information',
      contact: true
    }
  ]
};

const TERMS_CONDITIONS = {
  title: 'Terms and Conditions',
  icon: FileText,
  lastUpdated: '14 Aug 2026',
  sections: [
    {
      type: 'intro',
      text: 'Welcome to hhouseofbreadss ("we," "us," "our," or "the Bakery"). We operate the website hhouseofbreadss.com (the "Site"). By accessing or using our website and services, you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree with any part of these Terms, please do not use our website or services.'
    },
    {
      heading: '1. Introduction',
      bullets: [
        '1.1 These Terms govern your use of the website hhouseofbreadss.com and the purchase of bakery products and related services offered by hhouseofbreadss.',
        '1.2 By placing an order through our website, you confirm that you have read, understood, and agree to be bound by these Terms along with our Privacy Policy and Refund & Cancellation Policy.',
        '1.3 hhouseofbreadss reserves the right to update or modify these Terms at any time without prior notice. Continued use of the website after any changes constitutes your acceptance of the revised Terms.'
      ]
    },
    {
      heading: '2. Definitions',
      bullets: [
        '"Bakery" refers to hhouseofbreadss, the owner and operator of this website.',
        '"User / Customer / You" refers to any individual visiting or making a purchase through hhouseofbreadss.com.',
        '"Products" refers to bakery items including but not limited to breads, cakes, pastries, cookies, and other baked goods offered on the website.',
        '"Payment Gateway" refers to third-party online payment service providers including but not limited to RazorPay, UPI, credit/debit card networks, and net banking platforms.',
        '"Order" refers to a request placed by the Customer for the purchase of Products through the website.'
      ]
    },
    {
      heading: '3. Eligibility & Account',
      bullets: [
        '3.1 You must be at least 18 years of age to use this website and make purchases. By using this site, you represent and warrant that you meet this age requirement.',
        '3.2 You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
        '3.3 You agree to provide accurate, current, and complete information during registration and the checkout process.'
      ]
    },
    {
      heading: '4. Products, Pricing & Availability',
      bullets: [
        '4.1 hhouseofbreadss strives to display Products and their descriptions as accurately as possible. However, we do not warrant that product descriptions, images, or other content are entirely accurate, complete, or error-free.',
        '4.2 All prices listed on the website are in Indian Rupees (INR) and are subject to change without prior notice.',
        '4.3 Additional charges such as delivery fees, if applicable, will be displayed separately at checkout.',
        '4.4 Availability of Products is subject to stock and seasonal factors. hhouseofbreadss reserves the right to limit quantities or refuse any order at its sole discretion.',
        '4.5 The actual appearance of bakery products may vary from images shown on the website due to the handcrafted nature of our Products.'
      ]
    },
    {
      heading: '5. Orders & Fulfillment',
      bullets: [
        '5.1 When you place an Order, you will receive an order confirmation email/SMS acknowledging receipt. This acknowledgment does not constitute acceptance of the Order.',
        '5.2 hhouseofbreadss reserves the right to accept or decline any Order at its sole discretion. An Order is considered accepted only when dispatched/confirmed by us.',
        '5.3 Estimated delivery times are approximate. hhouseofbreadss is not liable for delays caused by factors beyond its control including weather conditions, traffic, courier delays, or force majeure events.',
        '5.4 The Customer must provide a complete and accurate delivery address. hhouseofbreadss shall not be responsible for non-delivery or mis-delivery arising from incorrect address details provided by the Customer.'
      ]
    },
    {
      heading: '6. Online Payments & Payment Gateway',
      bullets: [
        '6.1 hhouseofbreadss accepts online payments through secure payment gateways including RazorPay, UPI, credit cards, debit cards, net banking, and digital wallets.',
        '6.2 By making a payment, you authorize hhouseofbreadss and its Payment Gateway partners to charge the total amount (including applicable taxes and delivery fees) to your chosen payment method.',
        '6.3 All payments are processed through secure, PCI-DSS compliant third-party payment gateways. hhouseofbreadss does not store, process, or retain your credit/debit card details, CVV, OTP, or any sensitive financial information on its servers.',
        '6.4 Any payment-related disputes or unauthorized charges should be reported to the respective Payment Gateway provider and to hhouseofbreadss at hhouseofbreadss@gmail.com within 7 days of the transaction.',
        '6.5 hhouseofbreadss shall not be liable for any payment processing errors, delays, or failures caused by the Payment Gateway provider or the Customer\'s bank.',
        '6.6 In case of a failed transaction where the amount has been debited but the Order is not confirmed, a refund (if applicable) will be initiated by the Payment Gateway as per their standard processing timeline.',
        '6.7 Fraudulent or suspicious payment activity may result in immediate cancellation of the Order and reporting to relevant authorities.'
      ]
    },
    {
      heading: '7. Refunds & Cancellations',
      bullets: [
        '7.1 Cancellations by Customer: Orders can be cancelled before they have been dispatched/prepared. Once a Product has been prepared or dispatched, cancellation is not possible as bakery items are perishable and made-to-order.',
        '7.2 Cancellations by hhouseofbreadss: We reserve the right to cancel any Order due to unavailability of Products, pricing errors, payment issues, or any other valid reason. A full refund will be initiated in such cases.',
        '7.3 Refunds (if applicable) will be processed through the original payment method within 7–10 business days, subject to the Payment Gateway\'s processing timeline.',
        '7.4 Due to the perishable nature of bakery Products, returns are not accepted unless the Product delivered is damaged, spoiled, or significantly different from what was ordered. The Customer must report the issue within 24 hours of delivery with photographic evidence.'
      ]
    },
    {
      heading: '8. Food Safety & Allergen Information',
      bullets: [
        '8.1 hhouseofbreadss takes food safety and hygiene seriously. All Products are prepared in a clean, hygienic environment.',
        '8.2 Our bakery Products may contain or come into contact with common allergens including wheat (gluten), dairy, nuts, soy, and sesame. Customers with food allergies or dietary restrictions are strongly advised to review product descriptions and allergen information before placing an Order.',
        '8.3 hhouseofbreadss is not liable for any allergic reactions, health issues, or adverse effects resulting from the consumption of Products. It is the Customer\'s responsibility to inform themselves of ingredients and allergens.',
        '8.4 All Products are best consumed within the recommended time frame indicated on the packaging or product description.'
      ]
    },
    {
      heading: '9. Intellectual Property',
      bullets: [
        '9.1 All content on hhouseofbreadss.com including text, graphics, logos, images, photographs, product designs, recipes, and software is the intellectual property of hhouseofbreadss and is protected under applicable copyright, trademark, and other intellectual property laws.',
        '9.2 You may not reproduce, distribute, modify, republish, or otherwise use any content from this website without prior written consent from hhouseofbreadss.',
        '9.3 The name "hhouseofbreadss," its logo, and all associated branding elements are trademarks of hhouseofbreadss. Unauthorized use is strictly prohibited.'
      ]
    },
    {
      heading: '10. User Conduct',
      text: 'You agree to use hhouseofbreadss.com only for lawful purposes. You shall not:',
      bullets: [
        'Use the website in any way that violates applicable laws or regulations.',
        'Attempt to gain unauthorized access to any part of the website, its systems, or databases.',
        'Introduce viruses, malware, or any other malicious code.',
        'Scrape, copy, or misuse content from the website.',
        'Place fraudulent or fake orders.',
        'Use the website to harass, abuse, or harm any other individual or entity.'
      ],
      footer: 'Violation of these terms may result in immediate termination of your access to the website and may subject you to legal action.'
    },
    {
      heading: '11. Privacy',
      text: 'Your use of the website is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. Please review our Privacy Policy carefully.'
    },
    {
      heading: '12. Disclaimers & Limitation of Liability',
      bullets: [
        '12.1 The website and Products are provided on an "as is" and "as available" basis. hhouseofbreadss does not make any warranties or representations, express or implied, regarding the website\'s operation, uninterrupted access, or error-free performance.',
        '12.2 To the fullest extent permitted by law, hhouseofbreadss shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of the website or Products.',
        '12.3 hhouseofbreadss\'s total liability for any claim shall not exceed the total amount paid by the Customer for the specific Order giving rise to the claim.',
        '12.4 hhouseofbreadss shall not be liable for any delays or disruptions caused by circumstances beyond its reasonable control, including acts of God, natural disasters, pandemics, government actions, labor strikes, or internet/service outages (Force Majeure).'
      ]
    },
    {
      heading: '13. Third-Party Links & Services',
      bullets: [
        '13.1 The website may contain links to third-party websites or use third-party services (e.g., Payment Gateways like RazorPay, delivery partners, social media plugins). hhouseofbreadss does not control and is not responsible for the content, policies, or practices of such third parties.',
        '13.2 Your interactions with third-party services through our website are solely at your own risk and subject to their respective terms and policies.'
      ]
    },
    {
      heading: '14. Governing Law & Dispute Resolution',
      bullets: [
        '14.1 These Terms shall be governed by and construed in accordance with the laws of India.',
        '14.2 Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Gurgaon, Haryana, India.',
        '14.3 Before initiating legal proceedings, the parties shall attempt to resolve any dispute amicably through good-faith discussions for a period of 30 days.'
      ]
    },
    {
      heading: '15. Termination',
      bullets: [
        '15.1 hhouseofbreadss reserves the right to suspend or terminate your access to the website at any time, without notice, for any reason including violation of these Terms.',
        '15.2 Upon termination, all provisions of these Terms which by their nature should survive termination shall remain in effect, including intellectual property, disclaimer, and limitation of liability clauses.'
      ]
    },
    {
      heading: '16. Severability',
      text: 'If any provision of these Terms is found to be unenforceable or invalid by a court of competent authority, that provision shall be deemed modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall remain in full force and effect.'
    },
    {
      heading: '17. Contact Information',
      contact: true
    }
  ]
};

const POLICIES = {
  'privacy-policy': PRIVACY_POLICY,
  'refund-policy': REFUND_POLICY,
  'terms-and-conditions': TERMS_CONDITIONS
};

// ─── RENDERER ─────────────────────────────────────────────────────────────────

const ContactBlock = () => (
  <div className="mt-3 bg-amber-50 rounded-lg p-4 space-y-1 text-sm">
    <p><span className="font-semibold">Business Name:</span> hhouseofbreadss</p>
    <p><span className="font-semibold">Website:</span> hhouseofbreadss.com</p>
    <p><span className="font-semibold">Email:</span>{' '}
      <a href="mailto:hhouseofbreadss@gmail.com" className="text-primary hover:underline">hhouseofbreadss@gmail.com</a>
    </p>
    <p><span className="font-semibold">Phone:</span>{' '}
      <a href="tel:+919999183418" className="text-primary hover:underline">+91 9999183418</a>
    </p>
    <p><span className="font-semibold">Address:</span> R3/015A, M3M Cosmopolitan, Sec 66, Gurugram, Haryana</p>
  </div>
);

const PolicyTable = ({ headers, rows }) => (
  <div className="overflow-x-auto my-4">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-amber-100">
          {headers.map((h) => (
            <th key={h} className="border border-amber-200 px-3 py-2 text-left font-semibold text-amber-900 whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50/50'}>
            {row.map((cell, j) => (
              <td key={j} className="border border-amber-100 px-3 py-2 text-muted-foreground align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mt-2 space-y-1.5 list-none">
    {items.map((item, i) => (
      <li key={i} className="flex gap-2 text-muted-foreground text-sm">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0"></span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const renderSubsection = (sub, idx) => (
  <div key={idx} className="mt-4 ml-2 pl-4 border-l-2 border-amber-100">
    <h4 className="font-semibold text-accent text-sm mb-2">{sub.subheading}</h4>
    {sub.text && <p className="text-muted-foreground text-sm">{sub.text}</p>}
    {sub.table && <PolicyTable {...sub.table} />}
    {sub.bullets && <BulletList items={sub.bullets} />}
    {sub.footer && <p className="mt-3 text-xs text-muted-foreground italic">{sub.footer}</p>}
  </div>
);

const renderSection = (section, idx) => {
  if (section.type === 'intro') {
    return (
      <p key={idx} className="text-muted-foreground leading-relaxed border-l-4 border-primary/30 pl-4 bg-amber-50/50 py-3 rounded-r-lg">
        {section.text}
      </p>
    );
  }

  return (
    <div key={idx} className="pt-2">
      <h3 className="text-base font-bold text-accent mb-3 mt-6">{section.heading}</h3>
      {section.text && <p className="text-muted-foreground text-sm mb-2">{section.text}</p>}
      {section.text2 && <p className="text-muted-foreground text-sm mb-2 mt-3">{section.text2}</p>}
      {section.table && <PolicyTable {...section.table} />}
      {section.bullets && <BulletList items={section.bullets} />}
      {section.subsections && section.subsections.map((sub, i) => renderSubsection(sub, i))}
      {section.footer && <p className="mt-3 text-xs text-muted-foreground italic">{section.footer}</p>}
      {section.contact && <ContactBlock />}
    </div>
  );
};

// ─── PAGE COMPONENT ───────────────────────────────────────────────────────────

export default function PolicyPage() {
  const location = useLocation();
  // Map pathname → policy key
  const policyKey = location.pathname.replace(/^\//, ''); // strip leading slash
  const policy = POLICIES[policyKey];

  if (!policy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-accent mb-4">Page not found</p>
          <Link to="/" className="text-primary hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = policy.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/30 to-white">
      {/* Header */}
      <div className="bg-white border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="policy-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Title Block */}
        <div className="flex items-start gap-4 mb-8 pb-6 border-b border-amber-100">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <IconComponent className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-accent">{policy.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">Last Updated: {policy.lastUpdated}</p>
            <p className="text-sm text-muted-foreground mt-0.5">hhouseofbreadss — hhouseofbreadss.com</p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-1">
          {policy.sections.map((section, idx) => renderSection(section, idx))}
        </div>

        {/* Footer copyright */}
        <div className="mt-12 pt-6 border-t border-amber-100 text-center text-sm text-muted-foreground">
          <p>© 2025 hhouseofbreadss. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-3">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund & Cancellations</Link>
            <Link to="/terms-and-conditions" className="hover:text-primary transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
